import { FormInstance } from 'antd';

export type CustomReport = {
	id: string;
	name: string;
	description: string;
	acronym: string;
	created_at: Date;
	updated_at: Date;
	deleted_at?: Date | null;
};

export type GetCustomReportByNameResponse = Pick<CustomReport, 'id' | 'name' | 'description' | 'acronym'>;

export interface State {
	form: FormInstance;
	reportId?: string;
	categoryId: string;
	currentStep: number;
	newReview?: boolean;
	newReviewCreated: boolean;
	lastReviewId?: string;
	openActionPlan: boolean;
	firstReview?: boolean;
	categoryResultId?: string;
	isTolerable: boolean;
	titleActionPlan?: string;
	files?: File[];
	resolution886?: Resolution886ReportResponse;
	customReport?: GetCustomReportByNameResponse;
	initialValues: InitialValuesProps;
	assessment: AssessmentResponse;
	report: Resolution886ReportResponse;
	lastReview?: ReviewResponse;
	review?: ReviewResponse;
	stepsNames: StepsNames;
	actionPlan?: ActionPlanResponse;
	previousReview?: PreviousReview;
	selectedReviewId?: string;
	selectedAssessmentId?: string;
	relatedReport?: RelatedReport | null;
	reviewConsolidated: boolean;
}

export interface Methods {
	handleCurrentStep: (step: number) => void;
	handleNextStep: () => void;
	handleActionPlan: (title?: string) => void;
	handlePreviousStep: () => void;
	setCategoryId: (categoryId: string) => void;
	handleStepNavigationClick: (step: number) => Promise<void>;
	setInitialValues: (values: InitialValuesProps) => void;
	setAssessment: (assessment: AssessmentResponse) => void;
	handleSetReport: (report: Resolution886ReportResponse) => void;
	handleSetLastReviewId: (id: string) => void;
	mapDataToInitialValues: (
		data: Resolution886ReportResponse,
		review: ReviewResponse,
		isNewReview: boolean
	) => InitialValuesProps;
	handleSetNewReview: () => void;
	handleSetLastReview: (review: ReviewResponse) => void;
	handleSetReportId: (id: string) => void;
	handleSetStepsNames: (stepsNames: StepsNames) => void;
	handleSetCategoryResultId: (id: string) => void;
	handleSetIsTolerable: (isTolerable: boolean) => void;
	handleSetReview: (review: ReviewResponse) => void;
	handleSetActionPlan: (actionPlan: ActionPlanResponse) => void;
	setNewReviewCreated: (value: boolean) => void;
	handleSetPreviousReview: (previousReview: PreviousReview) => void;
	handleSetSelectedReviewId: (reviewId: string) => void;
	handleSetSelectedAssessmentId: (assessmentId: string) => void;
	handleSetRelatedReport: (relatedReport: RelatedReport) => void;
	handleSetReviewConsolidated: (consolidated: boolean) => void;
}

export type Context = State & Methods;

export interface Resolution886ReportProviderProps {
	children: JSX.Element;
	resolution886?: Resolution886ReportResponse;
	customReport?: GetCustomReportByNameResponse;
}

export interface InitialValuesProps {
	id: string | null;
	file_list_id: string[];
	general_information: GeneralInformationValuesProps;
	evaluation: EvaluationValuesProps;
	action_plan: {};
	result: {};
}

export interface GeneralInformationValuesProps {
	review_id: string | null;
	name: string | null;
	workstation_id: string | null;
	sector_id: string | null;
	line_id: string | null;
	company_id: string | null;
	organization_id: string | null;
	employer_id: string | null;
	evaluator_id: string | null;
	occupational_doctor_id: string | null;
	safety_and_hygiene_responsible_id: string | null;
	symptom_location: string | null;
	workers_number: number | null;
	early_manifestation: boolean | null;
	written_work_procedure: boolean | null;
	training: boolean | null;
	worker_list_id: string[];
}

export interface EvaluationValuesProps {
	activities: ActivitiesValuesProps[];
}

export interface ActivitiesValuesProps {
	file_id: string;
	file_name: string;
	assessment_id: string | null;
	activity_name: string | null;
	activity_id: string | null;
	status: string | null;
}

type DefaultIdName = { id: string; name: string };

export type File = { id: string; original_name: string; generated_name: string };

type Company = DefaultIdName;
type Organization = DefaultIdName;
type Sector = DefaultIdName;
type Line = DefaultIdName;
type Workstation = DefaultIdName;
type Author = DefaultIdName;
type Worker = DefaultIdName;
type Employer = DefaultIdName;
type OccupationalDoctor = DefaultIdName;
type SafetyAndHygieneResponsible = DefaultIdName;
type Evaluator = DefaultIdName;

export interface ReviewResponse {
	id: string;
	name: string;
	consolidated: boolean;
	workers_number: number;
	written_work_procedure: number;
	training: number;
	early_manifestation: number;
	symptom_location: string;
	author: Author;
	employer: Employer;
	step?: number;
	safety_and_hygiene_responsible: SafetyAndHygieneResponsible;
	occupational_doctor: OccupationalDoctor;
	evaluator: Evaluator;
	workers: Worker[];
	files?: File[];
	assessments: AssessmentResponse[];
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
}

export interface Resolution886ReportResponse {
	id: string;
	consolidated: boolean;
	organization: Organization;
	company: Company;
	sector: Sector;
	line: Line;
	workstation: Workstation;
	author: Author;
	reviews: ReviewResponse[];
	comment?: string | null;
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
}

export interface AssessmentResponse {
	id: string;
	resolution_886_review_id: string;
	status: STATUS_ASSESSMENT;
	activity?: DefaultIdName;
	file: File;
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
}

enum STATUS_ASSESSMENT {
	NOT_AVALIATED = 'NOT_AVALIATED',
	AVALIATED = 'AVALIATED'
}

export interface StepsNames {
	key: string;
	steps: {
		exposure_time: string;
		mandatory_questions: string;
		conditional_questions: string;
	};
}

export type FormDataValues = {
	general_information: {
		organization_id: string;
		company_id: string;
		sector_id: string;
		line_id: string;
		workstation_id: string;
		name: string;
		workers_number: number;
		worker_list_id: string[];
		written_work_procedure: boolean;
		training: boolean;
		early_manifestation: boolean;
		symptom_location: string;
		evaluator_id: string;
		employer_id: string;
		occupational_doctor_id: string;
		safety_and_hygiene_responsible_id: string;
	};
} & any;

export enum StatusAssessment {
	NOT_AVALIATED = 'NOT_AVALIATED',
	AVALIATED = 'AVALIATED'
}

export interface ActionPlanResponse {
	id: string;
	title: string;
	due_date: Date;
}

export interface PreviousReview {
	id: string;
	name: string;
	consolidated: number;
	workers_number: number;
	written_work_procedure: number;
	training: number;
	early_manifestation: number;
	symptom_location: string;
	step?: number;
	author: DefaultIdName;
	employer: DefaultIdName;
	safety_and_hygiene_responsible: DefaultIdName;
	occupational_doctor: DefaultIdName;
	evaluator: DefaultIdName;
	workers: DefaultIdName[];
	files: File[];
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
}

export interface RelatedReport {
	name: string;
	result: Result;
}

export interface Result {
	id: string;
	risk: RiskLibertyMutual | string;
	custom_report_result_id?: string;
	additional_evaluation_option?: string;
	value?: string;
}

export interface RiskLibertyMutual {
	male_score: string;
	female_score: string;
}

export type Question = {
	key: string;
	text: string;
	required: boolean;
	depends_on?: string[];
	conditional_required?: boolean;
};

export type Category = {
	key: string;
	name: string;
	mandatory_questions: Question[];
	conditional_questions: Question[];
};

export interface Report {
	id: string;
	company?: {
		id: string;
	};
	organization?: {
		id: string;
	};
	reviews?: Array<{
		id: string;
	}>;
}

export interface Assessment {
	id: string;
	activity: {
		id: string;
		name: string;
	};
	category_results: CategoryResult[];
}

export interface CategoryResult {
	id: string | null;
	exposure_time: string | null;
	risk: string;
	observation: string | null;
	category: {
		id: string;
		name: string | null;
		key: string | null;
	};
	action_plan: {
		id: string;
		title: string;
		due_date: string;
		status: string;
	} | null;
	related_report?: {
		result?: {
			risk?: string;
		};
	};
}

export interface Step {
	step: {
		name: string;
		sequence: number;
	};
	questions_results?: Array<{
		value: string;
	}>;
}

export interface Task {
	title: string;
	responsible?: string;
	responsible_user?: {
		name: string;
	};
	responsible_name?: string;
	due_date: string;
}
